/* The derivation: query → one internal model that every endpoint projects
 * from, so the numbers tie out across the dashboard (summary.context_blocks
 * matches the session-detail block breakdown, per-tool result+call sum to the
 * tool's total, a breakdown's parts sum to its whole, etc.).
 *
 * Everything is seeded off the normalized query, so a given scope+window+filter
 * combination always yields the same figures — and different scopes look
 * genuinely different (a Bash-heavy infra repo ranks Bash on top; selecting the
 * baseline-passthrough config collapses the savings to ~zero). */

import {
  AGENTS,
  ALL_REPOS,
  BASE,
  BASH_COMMANDS,
  BLOCK_TYPES,
  COST_SHARES,
  MODELS,
  SYSTEM_SECTIONS,
  TOOLS,
  TOOL_DEF_WEIGHTS,
  TOTAL_AGENT_WEIGHT,
  TRANSFORMERS,
  UNREGISTERED_AGENTS,
  repoWeight,
} from "./catalog";
import { Rng, splitByWeights } from "./seed";
import { DASHBOARD_NOW } from "../clock";
import { readEnv } from "../config";
import type {
  CostBreakdown,
  LatencyStats,
  MassEntry,
  ModelUsage,
  TransformerStat,
} from "../types";

/* Empties the mock universe, so the states that only appear before an agent's
 * first rollup rows exist can be reviewed locally (OPSS-323). Set it in
 * .env.development.local; it does nothing against a real API base. */
const NO_TRAFFIC = readEnv("VITE_MOCK_NO_TRAFFIC") === "true";

// Implied $/Mtok rates, back-solved from the contract's worked example so token
// counts land at a realistic scale relative to the costs we generate.
const RATES = { input: 3.0, cache_read: 1.5, cache_write: 18.9, output: 24.0 };

const CONFIG_VOLUME_SHARE: Record<string, number> = {
  "everything-on": 0.4,
  "routing-tooldef-lossless": 0.2,
  "rtk-aggressive": 0.15,
  "cache-tuned": 0.15,
  "baseline-passthrough": 0.1,
};
const CONFIG_SAVED_PCT: Record<string, number> = {
  "everything-on": 0.38,
  "routing-tooldef-lossless": 0.33,
  "rtk-aggressive": 0.47,
  "cache-tuned": 0.29,
  "baseline-passthrough": 0.02,
};
const EXPERIMENT_VOLUME_SHARE = 0.06;

// Per-block byte-turn weights — intentionally NOT the cost weights, so the UI
// can surface "small in bytes, large in $" (the cached prefix) vs the opposite.
const BLOCK_BYTE_WEIGHTS: Record<string, number> = {
  tool_result: 0.52,
  text: 0.14,
  thinking: 0.11,
  tools: 0.1,
  tool_use: 0.08,
  system: 0.05,
};

const DAY_MS = 86_400_000;

export interface ResolvedQuery {
  fromMs: number;
  toMs: number;
  windowDays: number;
  repos: string[]; // active repo set (all repos if unfiltered)
  /** Whether `repo` was explicitly passed — so meta.filters echoes only what
   *  was actually applied, not the expanded "all repos" default. */
  reposExplicit: boolean;
  models: string[]; // selected models (empty = all)
  configs: string[];
  experiments: string[];
  routed?: boolean;
  sessionIds: string[];
  agentIds: string[];
  agentKinds: string[];
  seed: string;
}

export interface DerivedModel {
  resolved: ResolvedQuery;
  callCount: number;
  sessionCount: number;
  cost: CostBreakdown;
  tokens: {
    input: number;
    output: number;
    cache_read: number;
    cache_write: number;
  };
  bytesBefore: number;
  bytesAfter: number;
  savedPct: number;
  savingsScale: number;
  cache: {
    read_tokens: number;
    write_tokens: number;
    write_5m_tokens: number;
    write_1h_tokens: number;
    invalidations: number;
  };
  models: ModelUsage[];
  routing: {
    routed_calls: number;
    routed_pct: number;
    by_target: Array<{ target_model: string; calls: number }>;
  };
  latency: LatencyStats;
  blocks: MassEntry[];
  tools: MassEntry[];
  bash: MassEntry[];
  prefix: {
    tools_cost_usd: number;
    system_cost_usd: number;
    tools: MassEntry[];
    system_sections: MassEntry[];
  };
  transformers: TransformerStat[];
  /** Σ clawback over the context blocks — the headline "saved $". */
  savedUsd: number;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

export function resolveQuery(q: {
  from?: string;
  to?: string;
  repo?: string[];
  model?: string[];
  config_id?: string[];
  experiment?: string[];
  routed?: boolean;
  session_id?: string[];
  agent_id?: string[];
  agent_kind?: string[];
}): ResolvedQuery {
  const nowMs = DASHBOARD_NOW.getTime();
  const toMs = q.to ? new Date(q.to).getTime() : nowMs;
  const fromMs = q.from ? new Date(q.from).getTime() : toMs - 7 * DAY_MS;
  const windowDays = Math.max((toMs - fromMs) / DAY_MS, 1 / 24);
  const reposExplicit = !!(q.repo && q.repo.length);
  const repos = q.repo && q.repo.length ? q.repo : ALL_REPOS.map((r) => r.name);
  const models = q.model ?? [];
  const configs = q.config_id ?? [];
  const experiments = q.experiment ?? [];
  const sessionIds = q.session_id ?? [];
  const agentIds = q.agent_id ?? [];
  const agentKinds = q.agent_kind ?? [];

  const seed = JSON.stringify({
    f: Math.round(fromMs / 60000),
    t: Math.round(toMs / 60000),
    r: [...repos].sort(),
    m: [...models].sort(),
    c: [...configs].sort(),
    e: [...experiments].sort(),
    routed: q.routed ?? null,
    s: [...sessionIds].sort(),
    a: [...agentIds].sort(),
    ak: [...agentKinds].sort(),
  });

  return {
    fromMs,
    toMs,
    windowDays,
    repos,
    reposExplicit,
    models,
    configs,
    experiments,
    routed: q.routed,
    sessionIds,
    agentIds,
    agentKinds,
    seed,
  };
}

/** Fraction of all agent-attributed traffic captured by the active agent
 *  filters. 1 when no agent filter is set (org-wide / aggregate views). */
function agentShare(agentIds: string[], agentKinds: string[]): number {
  if (!agentIds.length && !agentKinds.length) return 1;

  const all = [
    ...AGENTS.map((a) => ({
      agent_id: a.agent_id,
      kind: a.kind as string,
      weight: a.weight,
    })),
    ...UNREGISTERED_AGENTS.map((a) => ({
      agent_id: a.agent_id,
      kind: a.kind as string,
      weight: a.weight,
    })),
  ];
  const matched = all
    .filter(
      (a) =>
        (!agentIds.length || agentIds.includes(a.agent_id)) &&
        (!agentKinds.length || agentKinds.includes(a.kind)),
    )
    .reduce((sum, a) => sum + a.weight, 0);
  return Math.min(1, matched / TOTAL_AGENT_WEIGHT);
}

function scopeShare(repos: string[]): number {
  const total = ALL_REPOS.reduce((a, r) => a + r.weight, 0);
  const active = repos.reduce((a, name) => a + repoWeight(name), 0);
  return Math.min(active / total, 1);
}

/** Average tool-mix bias across the active repos (read/bash/edit tilt). */
export function biasAverages(repos: string[]) {
  const defs = ALL_REPOS.filter((r) => repos.includes(r.name));
  const n = defs.length || 1;
  const sum = (k: "read" | "bash" | "edit") =>
    defs.reduce((a, r) => a + (r.bias?.[k] ?? 0), 0) / n;
  return { read: sum("read"), bash: sum("bash"), edit: sum("edit") };
}

function effectiveSavedPct(configs: string[], rng: Rng): number {
  let base = BASE.savedPct;
  if (configs.length) {
    base =
      configs.reduce((a, c) => a + (CONFIG_SAVED_PCT[c] ?? BASE.savedPct), 0) /
      configs.length;
  }

  return Math.max(0, Math.min(0.55, base * rng.jitter(0.08)));
}

function buildModelUsages(
  callCount: number,
  selected: string[],
  rng: Rng,
): ModelUsage[] {
  const pool = selected.length
    ? MODELS.filter((m) => selected.includes(m.name))
    : MODELS;
  const weights = pool.map((m) => m.callShare * rng.jitter(0.1));
  const calls = splitByWeights(callCount, weights, true);
  return pool.map((m, i) => ({
    name: m.name,
    calls: calls[i],
    cost_usd: round2(calls[i] * m.costPerCall * rng.jitter(0.06)),
  }));
}

export function buildCostDecomposition(total: number, rng: Rng): CostBreakdown {
  const weights = [
    COST_SHARES.input * rng.jitter(0.2),
    COST_SHARES.cache_read * rng.jitter(0.08),
    COST_SHARES.cache_write * rng.jitter(0.08),
    COST_SHARES.output * rng.jitter(0.1),
  ];
  const [input, cache_read, cache_write, output] = splitByWeights(
    total,
    weights,
  );
  // Round parts first, then derive total from them so the decomposition sums
  // exactly to total (contract §4: "Together they equal total").
  const parts = {
    input: round4(input),
    cache_read: round2(cache_read),
    cache_write: round2(cache_write),
    output: round2(output),
  };
  return {
    ...parts,
    total: round2(
      parts.input + parts.cache_read + parts.cache_write + parts.output,
    ),
  };
}

export function tokensFromCost(cost: CostBreakdown) {
  return {
    input: Math.round((cost.input / RATES.input) * 1e6),
    cache_read: Math.round((cost.cache_read / RATES.cache_read) * 1e6),
    cache_write: Math.round((cost.cache_write / RATES.cache_write) * 1e6),
    output: Math.round((cost.output / RATES.output) * 1e6),
  };
}

export function buildLatency(rng: Rng): LatencyStats {
  const tri = (avg: number, p50: number, p95: number) => ({
    avg: Math.round(avg * rng.jitter(0.08)),
    p50: Math.round(p50 * rng.jitter(0.08)),
    p95: Math.round(p95 * rng.jitter(0.1)),
  });
  return {
    gateway: tri(9100, 6800, 28000),
    pipeline: tri(12, 8, 40),
    upstream: tri(700, 520, 2200),
    stream_total: tri(9000, 6700, 27500),
  };
}

function entry(
  name: string,
  cost: number,
  totalCost: number,
  bytes: number,
  extra: Partial<MassEntry> = {},
): MassEntry {
  // The optional split/clawback fields default to null per the contract's
  // "null otherwise" wording (§5); `extra` fills them where they apply.
  return {
    name,
    cost_usd: round2(cost),
    cost_pct: round2(totalCost > 0 ? (cost / totalCost) * 100 : 0),
    bytes: Math.round(bytes),
    tokens_est: Math.round(bytes / BASE.bytesPerToken),
    result_cost_usd: null,
    call_cost_usd: null,
    result_bytes: null,
    call_bytes: null,
    cost_after_usd: null,
    clawback_usd: null,
    clawback_pct: null,
    ...extra,
  };
}

function buildBlocks(
  inputBill: number,
  bytesBefore: number,
  savingsScale: number,
  rng: Rng,
): MassEntry[] {
  const costWeights = BLOCK_TYPES.map((b) => b.share * rng.jitter(0.07));
  // The forwarded (post-pipeline) cost of the blocks sums to the input-side
  // bill — contract §4: Σ cost_after_usd == input + cache_read + cache_write.
  const afterCosts = splitByWeights(inputBill, costWeights);
  const byteWeights = BLOCK_TYPES.map(
    (b) => BLOCK_BYTE_WEIGHTS[b.name] * rng.jitter(0.07),
  );
  const bytes = splitByWeights(bytesBefore, byteWeights);
  // Clawback sits ON TOP of the forwarded cost, so cost_usd (the pre-pipeline
  // counterfactual) = cost_after + clawback, and Σ cost_usd == bill + savings.
  // cost_after/clawback are always numeric (0 where the pipeline can't shrink),
  // so the breakdown reconciles to the bill exactly.
  const claws = BLOCK_TYPES.map((b, i) => {
    const rate = b.clawbackRate * savingsScale;
    return rate > 0 ? afterCosts[i] * rate * rng.jitter(0.12) : 0;
  });
  const costs = afterCosts.map((after, i) => after + claws[i]);
  const grossTotal = costs.reduce((sum, c) => sum + c, 0);
  return BLOCK_TYPES.map((b, i) =>
    entry(b.name, costs[i], grossTotal, bytes[i], {
      cost_after_usd: round2(afterCosts[i]),
      clawback_usd: round2(claws[i]),
      clawback_pct: round2(costs[i] > 0 ? (claws[i] / costs[i]) * 100 : 0),
    }),
  ).sort((a, b) => b.cost_usd - a.cost_usd);
}

function splitResultCall(
  cost: number,
  bytes: number,
  resultRatio: number,
  clawbackRate: number,
  savingsScale: number,
  rng: Rng,
): Partial<MassEntry> {
  const resultCost = cost * resultRatio;
  const callCost = cost - resultCost;
  const claw = resultCost * clawbackRate * savingsScale * rng.jitter(0.12);
  const after = cost - claw;
  return {
    result_cost_usd: round2(resultCost),
    call_cost_usd: round2(callCost),
    result_bytes: Math.round(bytes * resultRatio),
    call_bytes: Math.round(bytes * (1 - resultRatio)),
    cost_after_usd: round2(after),
    clawback_usd: round2(claw),
    clawback_pct: round2(cost > 0 ? (claw / cost) * 100 : 0),
  };
}

function buildTools(
  massCost: number,
  massBytes: number,
  savingsScale: number,
  bias: { read: number; bash: number; edit: number },
  rng: Rng,
): MassEntry[] {
  const weights = TOOLS.map((t) => {
    const tilt = t.biasKey ? 1 + bias[t.biasKey] * 0.9 : 1;
    return t.share * tilt * rng.jitter(0.1);
  });
  const costs = splitByWeights(massCost, weights);
  const bytes = splitByWeights(massBytes, weights);
  return TOOLS.map((t, i) =>
    entry(
      t.name,
      costs[i],
      massCost,
      bytes[i],
      splitResultCall(
        costs[i],
        bytes[i],
        t.resultRatio,
        t.clawbackRate,
        savingsScale,
        rng,
      ),
    ),
  )
    .filter((e) => e.cost_usd > 0)
    .sort((a, b) => b.cost_usd - a.cost_usd);
}

function buildBash(
  massCost: number,
  massBytes: number,
  savingsScale: number,
  rng: Rng,
): MassEntry[] {
  const weights = BASH_COMMANDS.map((c) => c.share * rng.jitter(0.12));
  const costs = splitByWeights(massCost, weights);
  const bytes = splitByWeights(massBytes, weights);
  return BASH_COMMANDS.map((c, i) =>
    entry(
      c.name,
      costs[i],
      massCost,
      bytes[i],
      splitResultCall(
        costs[i],
        bytes[i],
        c.resultRatio,
        c.clawbackRate,
        savingsScale,
        rng,
      ),
    ),
  )
    .filter((e) => e.cost_usd > 0)
    .sort((a, b) => b.cost_usd - a.cost_usd);
}

function buildPrefix(
  toolsCost: number,
  systemCost: number,
  toolsBytes: number,
  systemBytes: number,
  rng: Rng,
) {
  const defNames = Object.keys(TOOL_DEF_WEIGHTS);
  const defWeights = defNames.map(
    (n) => TOOL_DEF_WEIGHTS[n] * rng.jitter(0.06),
  );
  const defCosts = splitByWeights(toolsCost, defWeights);
  const defBytes = splitByWeights(toolsBytes, defWeights);
  const tools = defNames
    .map((n, i) => entry(n, defCosts[i], toolsCost, defBytes[i]))
    .sort((a, b) => b.cost_usd - a.cost_usd);

  const secWeights = SYSTEM_SECTIONS.map((s) => s.share * rng.jitter(0.06));
  const secCosts = splitByWeights(systemCost, secWeights);
  const secBytes = splitByWeights(systemBytes, secWeights);
  const system_sections = SYSTEM_SECTIONS.map((s, i) =>
    entry(s.name, secCosts[i], systemCost, secBytes[i]),
  ).sort((a, b) => b.cost_usd - a.cost_usd);

  return {
    tools_cost_usd: round2(toolsCost),
    system_cost_usd: round2(systemCost),
    tools,
    system_sections,
  };
}

function buildTransformers(
  bytesBefore: number,
  callCount: number,
  savingsScale: number,
  rng: Rng,
): TransformerStat[] {
  return TRANSFORMERS.map((t) => {
    const runsTotal = Math.round(
      callCount * (0.6 + t.inputShare * 3.5) * rng.jitter(0.1),
    );
    const appliedPct = Math.min(100, t.appliedRate * savingsScale * 100);
    const runsApplied = Math.round(runsTotal * (appliedPct / 100));
    const bytesIn = Math.round(bytesBefore * t.inputShare * rng.jitter(0.08));
    const effSaving = t.savingRate * (t.savingRate > 0 ? savingsScale : 1);
    const bytesSaved = Math.round(bytesIn * effSaving * rng.jitter(0.08));
    const bytesOut = bytesIn - bytesSaved;
    const totalDuration = t.avgDurationMs * runsTotal;
    return {
      name: t.name,
      runs_total: runsTotal,
      runs_applied: runsApplied,
      applied_pct: round2(appliedPct),
      bytes_in: bytesIn,
      bytes_out: bytesOut,
      bytes_saved: bytesSaved,
      bytes_saved_pct: round2(bytesIn > 0 ? (bytesSaved / bytesIn) * 100 : 0),
      tokens_in_est: Math.round(bytesIn / BASE.bytesPerToken),
      tokens_out_est: Math.round(bytesOut / BASE.bytesPerToken),
      avg_duration_ms: round2(t.avgDurationMs * rng.jitter(0.05)),
      total_duration_ms: Math.round(totalDuration),
      invalidations:
        t.name === "router" || t.name === "tool-def-deduper"
          ? rng.int(0, 6)
          : 0,
      detail_numeric: t.detailNumeric?.(runsTotal, Math.abs(bytesSaved)) ?? {},
      detail_categorical: t.detailCategorical?.(runsTotal) ?? {},
    };
  });
}

export interface ContextBundle {
  blocks: MassEntry[];
  tools: MassEntry[];
  bash: MassEntry[];
  prefix: DerivedModel["prefix"];
  transformers: TransformerStat[];
}

/** Build the full context breakdown at any scale (org-wide or one session)
 *  from the input-side bill (input + cache_read + cache_write, i.e. cost.total
 *  − output) + byte budget. Σ block cost_after_usd == that bill; cost_usd adds
 *  clawback on top. Shared by buildModel and session detail so both stay
 *  internally consistent. */
export function buildContextBundle(
  inputBill: number,
  bytesBefore: number,
  savingsScale: number,
  bias: { read: number; bash: number; edit: number },
  callCount: number,
  rng: Rng,
): ContextBundle {
  const blocks = buildBlocks(inputBill, bytesBefore, savingsScale, rng);
  // All six block types are always present; fall back defensively to the first.
  const byName = (n: string) => blocks.find((b) => b.name === n) ?? blocks[0];
  const toolResult = byName("tool_result");
  const toolUse = byName("tool_use");
  const toolsBlock = byName("tools");
  const systemBlock = byName("system");

  const tools = buildTools(
    toolResult.cost_usd + toolUse.cost_usd,
    toolResult.bytes + toolUse.bytes,
    savingsScale,
    bias,
    rng,
  );
  const bashTool = tools.find((t) => t.name === "Bash");
  const bash = bashTool
    ? buildBash(bashTool.cost_usd, bashTool.bytes, savingsScale, rng)
    : [];

  const prefix = buildPrefix(
    toolsBlock.cost_usd,
    systemBlock.cost_usd,
    toolsBlock.bytes,
    systemBlock.bytes,
    rng,
  );

  const transformers = buildTransformers(
    bytesBefore,
    callCount,
    savingsScale,
    rng,
  );

  return { blocks, tools, bash, prefix, transformers };
}

/** The whole model for a query. Pure given its inputs. */
export function buildModel(resolved: ResolvedQuery): DerivedModel {
  const rng = new Rng(resolved.seed);

  // Volume factors stack multiplicatively, so filtering visibly narrows scope.
  const share = scopeShare(resolved.repos);
  const modelFactor = resolved.models.length
    ? MODELS.filter((m) => resolved.models.includes(m.name)).reduce(
        (a, m) => a + m.callShare,
        0,
      )
    : 1;
  const configFactor = resolved.configs.length
    ? resolved.configs.reduce((a, c) => a + (CONFIG_VOLUME_SHARE[c] ?? 0.1), 0)
    : 1;
  const experimentFactor = resolved.experiments.length
    ? Math.min(1, resolved.experiments.length * EXPERIMENT_VOLUME_SHARE)
    : 1;
  const routedFactor =
    resolved.routed === true ? 0.057 : resolved.routed === false ? 0.943 : 1;
  const agentFactor = agentShare(resolved.agentIds, resolved.agentKinds);

  const volume = NO_TRAFFIC
    ? 0
    : share *
      modelFactor *
      configFactor *
      experimentFactor *
      routedFactor *
      agentFactor;

  // Filters that match nothing report nothing: the floor of 1 exists so a
  // narrow-but-real slice never rounds away, and applying it to a zero slice
  // made an empty window indistinguishable from a quiet one.
  const callCount =
    volume === 0
      ? 0
      : Math.max(
          1,
          Math.round(
            BASE.callsPerDay * resolved.windowDays * volume * rng.jitter(0.1),
          ),
        );
  const sessionCount =
    callCount === 0
      ? 0
      : Math.max(
          1,
          Math.round(
            (callCount / (BASE.callsPerDay / BASE.sessionsPerDay)) *
              rng.jitter(0.08),
          ),
        );

  const models = buildModelUsages(callCount, resolved.models, rng);
  const totalCost = models.reduce((a, m) => a + m.cost_usd, 0);
  const cost = buildCostDecomposition(totalCost, rng);
  const tokensByClass = tokensFromCost(cost);
  const tokens = {
    input: tokensByClass.input,
    output: tokensByClass.output,
    cache_read: tokensByClass.cache_read,
    cache_write: tokensByClass.cache_write,
  };

  const savedPct = effectiveSavedPct(resolved.configs, rng);
  const savingsScale = Math.min(1.25, savedPct / BASE.savedPct);
  const bytesBefore = Math.round(
    callCount * BASE.bytesBeforePerCall * rng.jitter(0.08),
  );
  const bytesAfter = Math.round(bytesBefore * (1 - savedPct));

  const write5m = Math.round(tokens.cache_write * 0.88);
  const cache = {
    read_tokens: tokens.cache_read,
    write_tokens: tokens.cache_write,
    write_5m_tokens: write5m,
    write_1h_tokens: tokens.cache_write - write5m,
    invalidations: Math.round(sessionCount * 0.21 * rng.jitter(0.2)),
  };

  // Routing — re-routed calls go to the cheap target model(s).
  const targets = MODELS.filter(
    (m) =>
      m.routeTarget &&
      (!resolved.models.length || resolved.models.includes(m.name)),
  );
  const routedPct =
    resolved.routed === true
      ? 100
      : resolved.routed === false || !targets.length
        ? 0
        : round2(5.7 * rng.jitter(0.15));
  const routedCalls = Math.round(callCount * (routedPct / 100));
  const routing = {
    routed_calls: routedCalls,
    routed_pct: routedPct,
    by_target: targets.length
      ? splitByWeights(
          routedCalls,
          targets.map(() => 1 + rng.float()),
          true,
        ).map((calls, i) => ({ target_model: targets[i].name, calls }))
      : [],
  };

  const bias = biasAverages(resolved.repos);
  // Context is the input side only; output is its own cost line (contract §4).
  const inputBill = cost.input + cost.cache_read + cost.cache_write;
  const ctx = buildContextBundle(
    inputBill,
    bytesBefore,
    savingsScale,
    bias,
    callCount,
    rng,
  );
  const savedUsd = round2(
    ctx.blocks.reduce((a, b) => a + (b.clawback_usd ?? 0), 0),
  );

  return {
    resolved,
    callCount,
    sessionCount,
    cost,
    tokens,
    bytesBefore,
    bytesAfter,
    savedPct,
    savingsScale,
    cache,
    models,
    routing,
    latency: buildLatency(rng),
    ...ctx,
    savedUsd,
  };
}
